import { AppIcons, Tooltip, type AppIconName } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

export type SidebarIconRailItem = {
  icon: AppIconName;
  label: string;
  active?: boolean;
  onClick: () => void;
};

/**
 * Icon-only nav shown while the sidebar is collapsed. Each item is a 36px
 * icon button whose label rides in a tooltip (so the rail stays narrow);
 * `groups` are separated by a hairline divider and active items take the
 * terracotta tint. The list scrolls if a workspace has many projects.
 */
export function SidebarIconRail({
  groups,
}: Readonly<{ groups: SidebarIconRailItem[][] }>): React.JSX.Element {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto overflow-x-hidden px-2 pt-1.5">
      {groups
        .filter((items) => items.length > 0)
        .map((items, groupIndex) => (
          <div
            key={items.map((it) => it.label).join('|')}
            className="flex w-full flex-col items-center gap-1"
          >
            {groupIndex > 0 ? <div aria-hidden className="my-1 h-px w-6 bg-border-soft" /> : null}
            {items.map((it) => (
              <Tooltip key={it.label} label={it.label}>
                <button
                  type="button"
                  aria-label={it.label}
                  onClick={it.onClick}
                  className={twcn(
                    'grid h-9 w-9 flex-shrink-0 place-items-center rounded-md border-0 bg-transparent text-muted-foreground transition-colors duration-fast ease-standard hover:bg-subtle hover:text-foreground',
                    it.active && 'bg-primary-tint text-primary shadow-inset-primary-soft',
                  )}
                >
                  <AppIcons name={it.icon} size={18} />
                </button>
              </Tooltip>
            ))}
          </div>
        ))}
    </div>
  );
}
